import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Send, Trash2, CheckCircle2, RotateCcw } from 'lucide-react';
import {
  weeklyReportCreateSchema,
  type WeeklyReportInput,
} from '@bbl/validation';
import { apiRequest, ApiError } from '../lib/api';
import { useToast } from '../lib/toast';
import { useAuthStore } from '../lib/auth-store';
import { ListShell } from '../components/ListShell';
import { Button } from '../components/ui/Button';
import { Drawer } from '../components/ui/Drawer';
import { EmptyState } from '../components/ui/EmptyState';
import { ErrorState } from '../components/ui/ErrorState';
import { TextField, TextAreaField, SelectField } from '../components/ui/Field';
import { WeeklyReportStatusBadge } from '../components/academics/StatusBadge';
import {
  startOfWeekMonday,
  type BatchRecord,
  type WeeklyReport,
} from '../components/academics/types';
import type { StudentRecord } from '../components/people/types';
import { formatDate } from '../lib/format';

export const Route = createFileRoute('/_authed/academics/weekly-sheet')({
  component: WeeklySheetPage,
});

type Tab = 'review' | 'submit';

function WeeklySheetPage() {
  const role = useAuthStore((s) => s.user?.primaryRole ?? null);
  const canReview = role === 'super_admin' || role === 'academic_admin' || role === 'nalim';
  const canSubmit = role === 'teacher' || role === 'super_admin';

  const [tab, setTab] = useState<Tab>(canReview ? 'review' : 'submit');

  return (
    <ListShell
      title="Weekly sheets"
      description="Teachers submit, nalims review."
      filters={
        canReview && canSubmit ? (
          <TabBar tab={tab} onChange={setTab} />
        ) : undefined
      }
    >
      {tab === 'review' && canReview ? (
        <ReviewQueue />
      ) : (
        <TeacherPanel />
      )}
    </ListShell>
  );
}

function TabBar({ tab, onChange }: { tab: Tab; onChange: (next: Tab) => void }) {
  const options: ReadonlyArray<{ value: Tab; label: string }> = [
    { value: 'review', label: 'To review' },
    { value: 'submit', label: 'Submit my report' },
  ];
  return (
    <div role="tablist" aria-label="Weekly sheet view" className="flex flex-wrap items-center gap-2">
      {options.map((opt) => {
        const isActive = tab === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(opt.value)}
            className={
              isActive
                ? 'inline-flex items-center rounded-full border border-gold-500 bg-gold-50 px-3 py-1 text-xs font-medium text-gold-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500'
                : 'inline-flex items-center rounded-full border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] px-3 py-1 text-xs font-medium text-[color:var(--color-text-muted)] hover:border-gold-500 hover:text-[color:var(--color-text)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500'
            }
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Review queue (managers / admins)
// ---------------------------------------------------------------------------

function ReviewQueue() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [requestChangesFor, setRequestChangesFor] = useState<WeeklyReport | null>(null);

  const submittedQuery = useQuery({
    queryKey: ['weekly-sheet', 'submitted'] as const,
    queryFn: ({ signal }) =>
      apiRequest<{ items: WeeklyReport[] }>('/api/weekly-sheet', {
        signal,
        query: { status: 'submitted' },
      }),
  });

  const batchesQuery = useQuery({
    queryKey: ['batches', 'review-pool'] as const,
    queryFn: ({ signal }) =>
      apiRequest<{ items: BatchRecord[] }>('/api/batches', {
        signal,
        query: { page: 1, pageSize: 100 },
      }),
    staleTime: 60_000,
  });

  const batchLookup = useMemo(() => {
    const map = new Map<string, BatchRecord>();
    for (const b of batchesQuery.data?.items ?? []) map.set(b.id, b);
    return map;
  }, [batchesQuery.data]);

  const approveMutation = useMutation({
    mutationFn: (id: string) =>
      apiRequest<{ ok: true }>(`/api/weekly-sheet/${id}/approve`, { method: 'POST' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['weekly-sheet'] });
      toast('Report approved.', { variant: 'success' });
    },
    onError: (err: unknown) => {
      toast(err instanceof ApiError ? err.message : 'Could not approve.', {
        variant: 'error',
      });
    },
  });

  const items = submittedQuery.data?.items ?? [];

  if (submittedQuery.isError) {
    return (
      <ErrorState
        title="Could not load the sheets to review"
        description={
          submittedQuery.error instanceof ApiError
            ? submittedQuery.error.message
            : undefined
        }
        onRetry={() => submittedQuery.refetch()}
      />
    );
  }

  if (submittedQuery.isLoading) {
    return <p className="text-sm text-[color:var(--color-text-muted)]">Loading…</p>;
  }

  if (items.length === 0) {
    return (
      <EmptyState
        title="No reports waiting"
        description="When teachers submit weekly sheets, they will appear here for review."
      />
    );
  }

  return (
    <>
      <ul className="grid gap-3">
        {items.map((report) => {
          const batch = batchLookup.get(report.batchId);
          return (
            <li
              key={report.id}
              className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-4"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold">
                      {batch ? batch.name : 'Unknown batch'}
                    </h3>
                    <WeeklyReportStatusBadge status={report.status} />
                    <span className="text-xs text-[color:var(--color-text-muted)]">
                      Week of {formatDate(report.weekStartDate)}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-[color:var(--color-text)] whitespace-pre-wrap break-words">
                    {report.summary}
                  </p>
                  {report.challenges ? (
                    <p className="mt-2 text-xs text-[color:var(--color-text-muted)]">
                      <span className="font-semibold">Challenges:</span> {report.challenges}
                    </p>
                  ) : null}
                  {report.upcoming ? (
                    <p className="mt-1 text-xs text-[color:var(--color-text-muted)]">
                      <span className="font-semibold">Upcoming:</span> {report.upcoming}
                    </p>
                  ) : null}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<RotateCcw size={14} aria-hidden="true" />}
                    onClick={() => setRequestChangesFor(report)}
                  >
                    Request changes
                  </Button>
                  <Button
                    size="sm"
                    icon={<CheckCircle2 size={14} aria-hidden="true" />}
                    loading={
                      approveMutation.isPending && approveMutation.variables === report.id
                    }
                    onClick={() => approveMutation.mutate(report.id)}
                  >
                    Approve
                  </Button>
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      <RequestChangesDrawer
        report={requestChangesFor}
        onClose={() => setRequestChangesFor(null)}
        onDone={() => {
          void queryClient.invalidateQueries({ queryKey: ['weekly-sheet'] });
          setRequestChangesFor(null);
        }}
      />
    </>
  );
}

function RequestChangesDrawer({
  report,
  onClose,
  onDone,
}: {
  report: WeeklyReport | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reviewNotes, setReviewNotes] = useState('');
  const { toast } = useToast();
  const mutation = useMutation({
    mutationFn: () => {
      if (!report) throw new Error('No report');
      return apiRequest<{ ok: true }>(
        `/api/weekly-sheet/${report.id}/request-changes`,
        {
          method: 'POST',
          body: { reviewNotes: reviewNotes.trim() || undefined },
        },
      );
    },
    onSuccess: () => {
      toast('Changes requested. The teacher will be notified.', {
        variant: 'success',
      });
      setReviewNotes('');
      onDone();
    },
    onError: (err: unknown) => {
      toast(err instanceof ApiError ? err.message : 'Could not request changes.', {
        variant: 'error',
      });
    },
  });

  return (
    <Drawer
      open={report !== null}
      onClose={() => {
        setReviewNotes('');
        onClose();
      }}
      title="Request changes"
      size="md"
    >
      <div className="space-y-4">
        <p className="text-sm text-[color:var(--color-text-muted)]">
          Explain what needs to change. The teacher receives an in-app
          notification with your note.
        </p>
        <TextAreaField
          label="Review notes"
          rows={6}
          value={reviewNotes}
          onChange={(e) => setReviewNotes(e.target.value)}
        />
        <div className="flex justify-end gap-2">
          <Button
            variant="ghost"
            onClick={() => {
              setReviewNotes('');
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            loading={mutation.isPending}
            disabled={!report}
          >
            Send
          </Button>
        </div>
      </div>
    </Drawer>
  );
}

// ---------------------------------------------------------------------------
// Teacher submission panel
// ---------------------------------------------------------------------------

function TeacherPanel() {
  const [submitOpen, setSubmitOpen] = useState(false);

  const batchesQuery = useQuery({
    queryKey: ['batches', 'teacher-pool'] as const,
    queryFn: ({ signal }) =>
      apiRequest<{ items: BatchRecord[] }>('/api/batches', {
        signal,
        query: { page: 1, pageSize: 100 },
      }),
    staleTime: 60_000,
  });

  const reportsQuery = useQuery({
    queryKey: ['weekly-sheet', 'mine'] as const,
    queryFn: ({ signal }) =>
      apiRequest<{ items: WeeklyReport[] }>('/api/weekly-sheet', { signal }),
  });

  const weekStart = useMemo(() => startOfWeekMonday(), []);

  const batches = useMemo(
    () => (batchesQuery.data?.items ?? []).filter((b) => b.status === 'active'),
    [batchesQuery.data],
  );

  // Map of latest report per batch for the current week. If a batch has
  // multiple historical reports we take the most recent. The list endpoint
  // returns them ordered desc by weekStartDate so the first match wins.
  const thisWeekByBatch = useMemo(() => {
    const map = new Map<string, WeeklyReport>();
    for (const r of reportsQuery.data?.items ?? []) {
      if (r.weekStartDate !== weekStart) continue;
      if (!map.has(r.batchId)) map.set(r.batchId, r);
    }
    return map;
  }, [reportsQuery.data, weekStart]);

  if (reportsQuery.isError) {
    return (
      <ErrorState
        title="Could not load your reports"
        description={
          reportsQuery.error instanceof ApiError ? reportsQuery.error.message : undefined
        }
        onRetry={() => reportsQuery.refetch()}
      />
    );
  }

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="text-sm text-[color:var(--color-text-muted)]">
          Week of <span className="font-medium text-[color:var(--color-text)]">{formatDate(weekStart)}</span>
        </p>
        <Button
          icon={<Plus size={14} aria-hidden="true" />}
          onClick={() => setSubmitOpen(true)}
          disabled={batches.length === 0}
        >
          Submit weekly sheet
        </Button>
      </div>

      {batchesQuery.isLoading ? (
        <p className="text-sm text-[color:var(--color-text-muted)]">Loading batches…</p>
      ) : batches.length === 0 ? (
        <EmptyState
          title="No active batches"
          description="Once you’re assigned to an active batch you can submit weekly sheets here."
        />
      ) : (
        <ul className="grid gap-3">
          {batches.map((b) => {
            const existing = thisWeekByBatch.get(b.id);
            return (
              <li
                key={b.id}
                className="flex items-center justify-between gap-3 rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-4"
              >
                <div>
                  <h3 className="font-semibold">{b.name}</h3>
                  <p className="text-xs text-[color:var(--color-text-muted)]">
                    {existing ? `Submitted ${formatDate(existing.submittedAt ?? existing.createdAt)}` : 'Not submitted yet'}
                  </p>
                </div>
                {existing ? (
                  <WeeklyReportStatusBadge status={existing.status} />
                ) : (
                  <span className="text-xs text-[color:var(--color-text-muted)]">Pending</span>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <SubmitDrawer
        open={submitOpen}
        onClose={() => setSubmitOpen(false)}
        batches={batches}
        weekStart={weekStart}
      />
    </>
  );
}

interface SubmitDrawerProps {
  open: boolean;
  onClose: () => void;
  batches: ReadonlyArray<BatchRecord>;
  weekStart: string;
}

function SubmitDrawer({ open, onClose, batches, weekStart }: SubmitDrawerProps) {
  return (
    <Drawer open={open} onClose={onClose} title="Submit weekly sheet" size="lg">
      {open ? (
        <SubmitForm batches={batches} weekStart={weekStart} onDone={onClose} onCancel={onClose} />
      ) : null}
    </Drawer>
  );
}

function SubmitForm({
  batches,
  weekStart,
  onDone,
  onCancel,
}: {
  batches: ReadonlyArray<BatchRecord>;
  weekStart: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const studentsQuery = useQuery({
    queryKey: ['students', 'weekly-pool'] as const,
    queryFn: ({ signal }) =>
      apiRequest<{ items: StudentRecord[]; total: number }>('/api/students', {
        signal,
        query: { page: 1, pageSize: 100, status: 'active' },
      }),
    staleTime: 60_000,
  });

  const initialValues: WeeklyReportInput = {
    batchId: batches[0]?.id ?? '',
    weekStartDate: weekStart,
    summary: '',
    studentNotes: [],
  };

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<WeeklyReportInput>({
    resolver: zodResolver(weeklyReportCreateSchema),
    defaultValues: initialValues,
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'studentNotes',
  });

  const mutation = useMutation({
    mutationFn: (body: WeeklyReportInput) =>
      apiRequest<{ id: string }>('/api/weekly-sheet', { method: 'POST', body }),
  });

  const submit = handleSubmit(async (values) => {
    try {
      await mutation.mutateAsync(values);
      void queryClient.invalidateQueries({ queryKey: ['weekly-sheet'] });
      toast('Weekly sheet submitted.', { variant: 'success' });
      onDone();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Could not submit.', {
        variant: 'error',
      });
    }
  });

  const batchOptions = batches.map((b) => ({ value: b.id, label: b.name }));
  const studentOptions = useMemo(
    () => [
      { value: '', label: 'Select a student' },
      ...(studentsQuery.data?.items ?? []).map((s) => ({
        value: s.id,
        label: `${s.name} (${s.admissionNumber})`,
      })),
    ],
    [studentsQuery.data],
  );

  return (
    <form onSubmit={submit} noValidate className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          label="Batch"
          options={batchOptions}
          error={errors.batchId?.message}
          {...register('batchId')}
        />
        <TextField
          label="Week starting (Monday)"
          type="date"
          error={errors.weekStartDate?.message}
          {...register('weekStartDate')}
        />
      </div>
      <TextAreaField
        label="Summary"
        rows={4}
        error={errors.summary?.message}
        {...register('summary')}
      />
      <TextAreaField
        label="Challenges"
        optional
        rows={3}
        error={errors.challenges?.message}
        {...register('challenges', {
          setValueAs: (v: unknown) => {
            const s = typeof v === 'string' ? v.trim() : '';
            return s === '' ? undefined : s;
          },
        })}
      />
      <TextAreaField
        label="What’s upcoming next week"
        optional
        rows={3}
        error={errors.upcoming?.message}
        {...register('upcoming', {
          setValueAs: (v: unknown) => {
            const s = typeof v === 'string' ? v.trim() : '';
            return s === '' ? undefined : s;
          },
        })}
      />

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
            Per-student notes (optional)
          </h3>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            icon={<Plus size={14} aria-hidden="true" />}
            onClick={() => append({ studentId: '', note: '' })}
          >
            Add note
          </Button>
        </div>
        {fields.length === 0 ? (
          <p className="text-xs text-[color:var(--color-text-muted)]">
            Add focused notes for specific students if useful.
          </p>
        ) : (
          <div className="space-y-3">
            {fields.map((field, index) => (
              <div
                key={field.id}
                className="grid gap-3 sm:grid-cols-[1fr_2fr_auto] sm:items-end"
              >
                <Controller
                  control={control}
                  name={`studentNotes.${index}.studentId` as const}
                  render={({ field: f }) => (
                    <SelectField
                      label="Student"
                      options={studentOptions}
                      value={f.value}
                      onChange={(e) => f.onChange(e.target.value)}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name={`studentNotes.${index}.note` as const}
                  render={({ field: f }) => (
                    <TextField
                      label="Note"
                      value={f.value}
                      onChange={(e) => f.onChange(e.target.value)}
                    />
                  )}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  icon={<Trash2 size={14} aria-hidden="true" />}
                  onClick={() => remove(index)}
                  aria-label={`Remove note ${index + 1}`}
                >
                  Remove
                </Button>
              </div>
            ))}
          </div>
        )}
      </section>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="submit"
          loading={isSubmitting}
          icon={<Send size={14} aria-hidden="true" />}
        >
          Submit
        </Button>
      </div>
    </form>
  );
}

